import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Megaphone, Sparkles, Facebook, MessageCircle, Copy, CheckCircle2, Circle,
  ArrowRight, Rocket, Loader2, Music2, ExternalLink, RefreshCw, Lightbulb,
  Download, Facebook as FbIcon, Music2 as TkIcon, Link2, CheckCircle2 as Check2, Clock, Film, Share2, Upload, Gift,
} from "lucide-react";
import api, { apiError, fileUrl } from "@/lib/api";
import { VideoCreditsPanel } from "@/components/VideoCredits";

const scoreColor = (s) => (s >= 70 ? "#1B4332" : s >= 40 ? "#E85D04" : "#D00000");
const scoreLabel = (s) => (s >= 70 ? "Excellent !" : s >= 40 ? "Bon début" : "À améliorer");

function ScoreRing({ score }) {
  const r = 52, c = 2 * Math.PI * r;
  const off = c - (score / 100) * c;
  const col = scoreColor(score);
  return (
    <div className="relative h-36 w-36 shrink-0" data-testid="score-ring">
      <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
        <circle cx="60" cy="60" r={r} fill="none" stroke="#EDE7DD" strokeWidth="12" />
        <motion.circle
          cx="60" cy="60" r={r} fill="none" stroke={col} strokeWidth="12" strokeLinecap="round"
          strokeDasharray={c} initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: off }}
          transition={{ duration: 1, ease: "easeOut" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-4xl font-extrabold" style={{ color: col }} data-testid="score-value">{score}</span>
        <span className="text-xs text-[#9c968a] font-medium">/ 100</span>
      </div>
    </div>
  );
}

function CopyBtn({ text, testid }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setDone(true); toast.success("Texte copié !"); setTimeout(() => setDone(false), 1800); }
    catch { toast.error("Copie impossible."); }
  };
  return (
    <button data-testid={testid} onClick={copy} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#5C574F] hover:text-[#1A1A1A]">
      {done ? <CheckCircle2 className="h-3.5 w-3.5 text-[#1B4332]" /> : <Copy className="h-3.5 w-3.5" />} {done ? "Copié" : "Copier"}
    </button>
  );
}

export default function MarketingAssistant() {
  const navigate = useNavigate();
  const location = useLocation();
  const [analysis, setAnalysis] = useState(null);
  const [loadingA, setLoadingA] = useState(true);
  const [social, setSocial] = useState(null);
  const [copy, setCopy] = useState(null);
  const [genBusy, setGenBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [headline, setHeadline] = useState("");
  const [products, setProducts] = useState([]);
  const [videoProduct, setVideoProduct] = useState("");
  const [adVideo, setAdVideo] = useState(null);
  const [vidBusy, setVidBusy] = useState(false);
  const [vidStage, setVidStage] = useState("");
  const [videoScript, setVideoScript] = useState("");
  const [presenter, setPresenter] = useState(null);
  const [presenterBusy, setPresenterBusy] = useState(false);
  const presenterRef = useRef();
  const [presets, setPresets] = useState([]);
  useEffect(() => {
    api.get("/marketing/presenters").then(({ data }) => setPresets(data.presenters || [])).catch(() => {});
  }, []);
  const [importBusy, setImportBusy] = useState(false);
  const videoImportRef = useRef();

  const importVideo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 30 * 1024 * 1024) { toast.error("Vidéo trop lourde (30 Mo maximum)."); return; }
    setImportBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAdVideo({ video: data.url, headline: "", product_name: "" });
      setVideoAccepted(true);
      toast.success("Vidéo importée ! Vous pouvez la repartager ci-dessous.");
    } catch { toast.error("Import impossible. Réessayez."); }
    finally { setImportBusy(false); }
  };

  const uploadPresenter = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPresenterBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPresenter({ url: data.url });
      toast.success("Photo du présentateur ajoutée !");
    } catch { toast.error("Envoi de la photo impossible. Réessayez."); }
    finally { setPresenterBusy(false); }
  };
  const [videoAccepted, setVideoAccepted] = useState(false);
  const [credits, setCredits] = useState(null);
  const loadCredits = async () => {
    try { const { data } = await api.get("/marketing/credits"); setCredits(data); } catch { /* ignore */ }
  };
  useEffect(() => { loadCredits(); }, []);

  useEffect(() => {
    const sid = new URLSearchParams(location.search).get("credits_session_id");
    if (!sid) return;
    (async () => {
      try {
        const { data } = await api.get(`/marketing/credits/checkout-status/${sid}`);
        if (data.payment_status === "paid") toast.success("Paiement confirmé — vos crédits vidéo ont été ajoutés !");
        else toast.info("Paiement non finalisé. Réessayez si besoin.");
      } catch { /* ignore */ }
      loadCredits();
      navigate("/promouvoir", { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, navigate]);
  const launchRef = useRef(null);

  const loadAnalysis = async () => {
    setLoadingA(true);
    try { const { data } = await api.get("/marketing/analyze"); setAnalysis(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Analyse impossible."); }
    finally { setLoadingA(false); }
  };
  useEffect(() => { loadAnalysis(); }, []);

  const loadSocial = async () => {
    try { const { data } = await api.get("/social/status"); setSocial(data); } catch { /* ignore */ }
  };
  useEffect(() => { loadSocial(); }, []);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  useEffect(() => {
    const p = new URLSearchParams(location.search).get("social");
    if (!p) return;
    if (p.endsWith("_ok")) { toast.success("Compte connecté avec succès !"); loadSocial(); }
    else if (p.endsWith("_error")) toast.error("La connexion a échoué. Réessayez.");
    navigate("/promouvoir", { replace: true });
  }, [location.search, navigate]);

  const connectSocial = async (platform) => {
    try {
      const { data } = await api.get(`/social/${platform}/oauth/start`);
      window.location.href = data.url;
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Connexion indisponible."); }
  };
  const boostAuto = async (platform) => {
    try {
      await api.post("/social/boost", { platform, headline: headline.trim() });
      toast.success("Campagne lancée !");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Lancement indisponible."); }
  };

  const generate = async () => {
    setGenBusy(true);
    try {
      const { data } = await api.post("/marketing/ad-copy", {});
      setCopy(data);
      toast.success("Vos textes publicitaires sont prêts !");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Génération impossible."); }
    finally { setGenBusy(false); }
  };

  const url = analysis?.store_url || copy?.store_url || "";
  const hashtags = (copy?.hashtags || []).join(" ");

  const genVideo = async () => {
    if (!presenter?.url) { toast.error("Ajoutez d'abord la photo du présentateur."); return; }
    setVidBusy(true);
    setVideoAccepted(false);
    setVidStage("video");
    try {
      const { data } = await api.post("/marketing/ad-video", {
        product_id: videoProduct || undefined,
        headline: headline.trim(),
        script: videoScript.trim(),
        presenter_url: presenter.url,
      });
      const jobId = data.job_id;
      const started = Date.now();
      while (Date.now() - started < 14 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 4000));
        const { data: job } = await api.get(`/marketing/ad-video/${jobId}`);
        if (job.stage) setVidStage(job.stage);
        if (job.status === "completed") {
          setAdVideo(job);
          loadCredits();
          toast.success("Votre vidéo publicitaire est prête ! Regardez-la puis acceptez ou refaites.");
          return;
        }
        if (job.status === "failed") {
          loadCredits();
          toast.error(job.error || "Génération de la vidéo impossible.");
          return;
        }
      }
      toast.error("La génération prend trop de temps. Réessayez dans un instant.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Génération de la vidéo impossible."); }
    finally { setVidBusy(false); }
  };

  const shareVideoFile = async () => {
    if (!adVideo) return;
    const text = `${adVideo.headline || headline || "Découvrez ma boutique"}\n\n${url}`;
    try {
      const res = await fetch(fileUrl(adVideo.video));
      const blob = await res.blob();
      const file = new File([blob], "publicite-successnet.mp4", { type: "video/mp4" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text });
        return;
      }
    } catch { /* fallback ci-dessous */ }
    window.open(fileUrl(adVideo.video), "_blank");
  };

  const pickForLaunch = (text) => {
    setSelected(text);
    setTimeout(() => launchRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const shareWhatsApp = (text) => window.open(`https://wa.me/?text=${encodeURIComponent(`${text}\n\n${url}`)}`, "_blank");
  const shareFacebook = (text) => {
    navigator.clipboard?.writeText(`${text}\n\n${url}`).catch(() => {});
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, "_blank");
  };
  const openBoost = () => {
    navigator.clipboard?.writeText(`${selected || (copy?.variants?.[0]?.text) || ""}\n\n${url}`).catch(() => {});
    toast.success("Texte copié — collez-le dans Meta.");
    window.open("https://www.facebook.com/business/tools/ads-manager", "_blank");
  };

  return (
    <div className="px-4 pt-6 pb-28">
      <div className="flex items-center gap-2 mb-1">
        <span className="h-10 w-10 rounded-2xl bg-[#E85D04] flex items-center justify-center">
          <Megaphone className="h-6 w-6 text-white" />
        </span>
        <h1 className="font-display text-2xl font-extrabold tracking-tight" data-testid="marketing-title">Assistant Boutique Pro</h1>
      </div>
      <p className="text-sm text-[#5C574F] mb-5">Analysez votre boutique, générez des publicités percutantes et attirez plus de clients.</p>

      {/* ÉTAPE 1 — Analyse */}
      <section className="bg-white rounded-2xl border border-black/5 shadow-sm p-5 mb-4" data-testid="step-analyze">
        <p className="text-[11px] font-bold uppercase tracking-widest text-[#E85D04] mb-3">Étape 1 · Analyse</p>
        {loadingA ? (
          <div className="flex items-center gap-2 text-[#9c968a] text-sm py-8 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Analyse de votre boutique…</div>
        ) : analysis ? (
          <>
            <div className="flex items-center gap-5">
              <ScoreRing score={analysis.score} />
              <div>
                <p className="font-display text-lg font-extrabold" style={{ color: scoreColor(analysis.score) }}>{scoreLabel(analysis.score)}</p>
                <p className="text-sm text-[#5C574F] mt-1">Votre boutique a un score de <b>{analysis.score}/100</b>. Améliorez les points ci-dessous pour augmenter vos ventes.</p>
                <button data-testid="refresh-analyze-btn" onClick={loadAnalysis} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#E85D04]"><RefreshCw className="h-3.5 w-3.5" /> Réanalyser</button>
              </div>
            </div>

            {analysis.top_tips?.length > 0 && (
              <div className="mt-4 rounded-xl bg-[#FFF6EE] border border-[#F3D9C2] p-3" data-testid="top-tips">
                <p className="text-xs font-bold text-[#E85D04] flex items-center gap-1.5 mb-1.5"><Lightbulb className="h-4 w-4" /> 3 priorités pour progresser</p>
                <ul className="space-y-1.5">
                  {analysis.top_tips.map((tip, i) => (
                    <li key={tip} className="text-xs text-[#5C574F] flex gap-2" data-testid={`tip-${i}`}><span className="text-[#E85D04] font-bold">{i + 1}.</span> {tip}</li>
                  ))}
                </ul>
                <button data-testid="go-settings-btn" onClick={() => navigate("/reglages")} className="mt-2.5 inline-flex items-center gap-1 text-xs font-semibold text-[#1B4332]">Améliorer ma boutique <ArrowRight className="h-3.5 w-3.5" /></button>
              </div>
            )}

            <div className="mt-4 space-y-2" data-testid="checklist">
              {analysis.checks.map((c) => (
                <div key={c.key} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    {c.done ? <CheckCircle2 className="h-4 w-4 text-[#1B4332]" /> : <Circle className="h-4 w-4 text-[#cfc8ba]" />}
                    <span className={c.done ? "text-[#1A1A1A]" : "text-[#5C574F]"}>{c.label}</span>
                  </span>
                  <span className="text-xs font-semibold text-[#9c968a]">{c.earned}/{c.points}</span>
                </div>
              ))}
            </div>
          </>
        ) : null}
      </section>

      {/* ÉTAPE 2 — Textes publicitaires */}
      <section className="bg-white rounded-2xl border border-black/5 shadow-sm p-5 mb-4" data-testid="step-copy">
        <p className="text-[11px] font-bold uppercase tracking-widest text-[#E85D04] mb-1">Étape 2 · Textes publicitaires</p>
        <p className="text-sm text-[#5C574F] mb-3">L'IA rédige des accroches pour Facebook, Instagram, WhatsApp et TikTok à partir de votre catalogue.</p>
        <button
          data-testid="generate-copy-btn"
          onClick={generate}
          disabled={genBusy}
          className="w-full h-12 rounded-2xl bg-[#1B4332] text-white font-semibold flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50"
        >
          {genBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
          {genBusy ? "Rédaction en cours…" : copy ? "Régénérer mes textes" : "Générer mes textes publicitaires"}
        </button>

        {copy && (
          <div className="mt-4 space-y-3" data-testid="ad-copy-results">
            {copy.variants.map((v, i) => (
              <div key={v.label} className="rounded-xl border border-[#E5E0D8] p-3" data-testid={`variant-${i}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-bold uppercase tracking-wide text-[#E85D04]">{v.label}</span>
                  <CopyBtn text={`${v.text}\n\n${url}`} testid={`variant-copy-${i}`} />
                </div>
                <p className="text-sm text-[#1A1A1A] leading-relaxed whitespace-pre-line">{v.text}</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <button data-testid={`variant-wa-${i}`} onClick={() => shareWhatsApp(v.text)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#25D366] px-3 h-8 rounded-lg"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</button>
                  <button data-testid={`variant-fb-${i}`} onClick={() => shareFacebook(v.text)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#1877F2] px-3 h-8 rounded-lg"><Facebook className="h-3.5 w-3.5" /> Facebook</button>
                  <button data-testid={`variant-launch-${i}`} onClick={() => pickForLaunch(v.text)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#E85D04] border border-[#E85D04] px-3 h-8 rounded-lg"><Rocket className="h-3.5 w-3.5" /> Booster ce texte</button>
                </div>
              </div>
            ))}

            {copy.whatsapp && (
              <div className="rounded-xl border border-[#E5E0D8] p-3" data-testid="wa-broadcast">
                <div className="flex items-center justify-between mb-1.5"><span className="text-xs font-bold uppercase tracking-wide text-[#25D366]">Statut WhatsApp</span><CopyBtn text={`${copy.whatsapp}\n\n${url}`} testid="wa-copy" /></div>
                <p className="text-sm text-[#1A1A1A] leading-relaxed whitespace-pre-line">{copy.whatsapp}</p>
              </div>
            )}
            {copy.tiktok && (
              <div className="rounded-xl border border-[#E5E0D8] p-3" data-testid="tiktok-caption">
                <div className="flex items-center justify-between mb-1.5"><span className="text-xs font-bold uppercase tracking-wide text-[#1A1A1A] flex items-center gap-1"><Music2 className="h-3.5 w-3.5" /> TikTok</span><CopyBtn text={`${copy.tiktok}\n\n${url}`} testid="tiktok-copy" /></div>
                <p className="text-sm text-[#1A1A1A] leading-relaxed whitespace-pre-line">{copy.tiktok}</p>
              </div>
            )}
            {hashtags && (
              <div className="rounded-xl bg-[#F9F6F0] p-3" data-testid="hashtags">
                <div className="flex items-center justify-between mb-1"><span className="text-xs font-bold text-[#5C574F]">Hashtags</span><CopyBtn text={hashtags} testid="hashtags-copy" /></div>
                <p className="text-sm text-[#1877F2]">{hashtags}</p>
              </div>
            )}
          </div>
        )}
      </section>

      {/* ÉTAPE 2b — Vidéo publicitaire */}
      <section className="bg-white rounded-2xl border border-black/5 shadow-sm p-5 mb-4" data-testid="step-video">
        <p className="text-[11px] font-bold uppercase tracking-widest text-[#E85D04] mb-1 flex items-center gap-1.5"><Film className="h-3.5 w-3.5" /> Vidéo publicitaire IA</p>
        <p className="text-sm text-[#5C574F] mb-3">L'IA crée une vidéo façon influenceur : un présentateur (vous, par exemple !) montre votre produit face caméra et le recommande en français, comme sur TikTok. 15 secondes, format story vertical, avec la voix.</p>

        <VideoCreditsPanel credits={credits} onChanged={loadCredits} />

        <label className="text-xs font-semibold text-[#5C574F] mb-1 block">Produit à mettre en avant</label>
        <select
          data-testid="ad-video-product"
          value={videoProduct}
          onChange={(e) => setVideoProduct(e.target.value)}
          className="w-full h-12 bg-[#F9F6F0] rounded-xl px-4 text-sm outline-none border border-transparent focus:border-[#E85D04] mb-3"
        >
          <option value="">Choisir automatiquement (1er produit)</option>
          {products.map((p) => (
            <option key={p.product_id} value={p.product_id}>{p.name}</option>
          ))}
        </select>

        <input
          data-testid="ad-video-headline"
          value={headline}
          onChange={(e) => setHeadline(e.target.value)}
          placeholder="Message (optionnel — sinon l'IA le propose)"
          maxLength={60}
          className="w-full h-12 bg-[#F9F6F0] rounded-xl px-4 text-sm outline-none border border-transparent focus:border-[#E85D04] mb-3"
        />
        <label className="text-xs font-semibold text-[#5C574F] mb-1 block">Photo du présentateur / de la présentatrice *</label>
        <div className="flex gap-2 overflow-x-auto pb-1 mb-2" data-testid="presenter-presets">
          {presets.map((p) => (
            <button
              key={p.id}
              data-testid={`presenter-preset-${p.id}`}
              onClick={() => { setPresenter({ url: p.url, preset: p.id }); }}
              disabled={vidBusy}
              className={`shrink-0 flex flex-col items-center gap-1 ${presenter?.preset === p.id ? "" : "opacity-90"}`}
            >
              <img
                src={fileUrl(p.url)}
                alt={p.label}
                className={`h-14 w-14 rounded-full object-cover border-2 ${presenter?.preset === p.id ? "border-[#E85D04]" : "border-transparent"}`}
              />
              <span className={`text-[10px] font-semibold ${presenter?.preset === p.id ? "text-[#E85D04]" : "text-[#9c968a]"}`}>{p.label}</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 mb-3">
          {presenter?.url && !presenter?.preset ? (
            <img src={fileUrl(presenter.url)} alt="Présentateur" className="h-16 w-16 rounded-xl object-cover border border-[#E5E0D8]" data-testid="presenter-preview" />
          ) : !presenter?.url ? (
            <div className="h-16 w-16 rounded-xl bg-[#F9F6F0] border border-dashed border-[#C9C2B6] flex items-center justify-center text-[#9c968a] text-[10px] text-center px-1">Aucune photo</div>
          ) : null}
          <div className="flex-1">
            <button
              data-testid="presenter-upload-btn"
              onClick={() => presenterRef.current?.click()}
              disabled={presenterBusy || vidBusy}
              className="h-10 px-4 rounded-xl bg-[#F9F6F0] border border-[#E5E0D8] text-sm font-semibold text-[#1A1A1A] flex items-center gap-2 disabled:opacity-50"
            >
              {presenterBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {presenter?.url && !presenter?.preset ? "Changer ma photo" : "Ou utiliser ma propre photo"}
            </button>
            <p className="text-[11px] text-[#9c968a] mt-1">Choisissez un présentateur ci-dessus, ou envoyez une photo nette de face (vous, par exemple !).</p>
          </div>
          <input ref={presenterRef} type="file" accept="image/*" className="hidden" onChange={uploadPresenter} data-testid="presenter-file-input" />
        </div>

        <textarea
          data-testid="ad-video-script"
          value={videoScript}
          onChange={(e) => setVideoScript(e.target.value)}
          placeholder={"Que doit dire le présentateur ? (optionnel — sinon l'IA improvise)\nEx. : Franchement les amis, ce whisky est incroyable, et il est à 49,99$ seulement chez nous !"}
          maxLength={400}
          rows={3}
          className="w-full bg-[#F9F6F0] rounded-xl px-4 py-3 text-sm outline-none border border-transparent focus:border-[#E85D04] mb-3 resize-none"
        />
        <button
          data-testid="generate-video-btn"
          onClick={genVideo}
          disabled={vidBusy || (credits && !credits.first_video_free && credits.balance < (credits.video_cost ?? 5))}
          className="w-full h-12 rounded-2xl bg-[#1B4332] text-white font-semibold flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50"
        >
          {vidBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : credits?.first_video_free ? <Gift className="h-5 w-5" /> : <Film className="h-5 w-5" />}
          {vidBusy
            ? "Création en cours…"
            : credits?.first_video_free
              ? "Générer ma 1ʳᵉ vidéo — offerte !"
              : credits && credits.balance < (credits.video_cost ?? 5)
                ? `Crédits insuffisants (${credits.video_cost ?? 5} requis) — rechargez ci-dessus`
                : adVideo ? `Générer une autre vidéo (${credits?.video_cost ?? 5} cr.)` : `Générer une vidéo publicitaire (${credits?.video_cost ?? 5} cr.)`}
        </button>
        {vidBusy && (
          <p className="text-[11px] text-[#9c968a] mt-2 text-center" data-testid="ad-video-progress">
            L'IA tourne votre vidéo avec le présentateur, la voix et le produit… 5 à 8 minutes, merci de patienter.
          </p>
        )}

        <button
          data-testid="import-video-btn"
          onClick={() => videoImportRef.current?.click()}
          disabled={vidBusy || importBusy}
          className="w-full h-10 mt-2 rounded-2xl border border-[#E5E0D8] bg-white text-[#5C574F] text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {importBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {importBusy ? "Import en cours…" : "Importer une ancienne vidéo de mon téléphone (gratuit)"}
        </button>
        <input ref={videoImportRef} type="file" accept="video/*" className="hidden" onChange={importVideo} data-testid="video-import-input" />

        {adVideo && !vidBusy && (
          <div className="mt-4" data-testid="ad-video-result">
            {adVideo.headline && <p className="text-sm font-semibold text-[#1A1A1A] mb-2" data-testid="ad-video-message">« {adVideo.headline} »</p>}
            <video
              src={fileUrl(adVideo.video)}
              controls autoPlay loop muted playsInline
              className="w-full max-h-[70vh] object-contain rounded-xl border border-[#E5E0D8] bg-black"
              data-testid="ad-video-player"
            />
            <p className="text-[11px] text-[#9c968a] mt-1 text-center" data-testid="ad-video-sound-hint">🔊 Cette vidéo a du son : activez-le avec le bouton du lecteur.</p>

            {!videoAccepted ? (
              <>
                <p className="text-sm text-[#5C574F] mt-3 mb-2 text-center">Cette vidéo vous convient-elle ?</p>
                <div className="flex gap-2">
                  <button
                    data-testid="ad-video-accept"
                    onClick={() => { setVideoAccepted(true); toast.success("Vidéo acceptée ! Vous pouvez la partager."); }}
                    className="flex-1 h-12 rounded-2xl bg-[#1B4332] text-white font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"
                  >
                    <Check2 className="h-5 w-5" /> Accepter
                  </button>
                  <button
                    data-testid="ad-video-redo"
                    onClick={genVideo}
                    className="flex-1 h-12 rounded-2xl border-2 border-[#E85D04] text-[#E85D04] font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"
                  >
                    <RefreshCw className="h-5 w-5" /> Refaire
                  </button>
                </div>
              </>
            ) : (
              <div data-testid="ad-video-share">
                <div className="flex items-center gap-1.5 text-[#1B4332] text-sm font-semibold mt-3 mb-2"><Check2 className="h-4 w-4" /> Vidéo acceptée — partagez-la !</div>
                <div className="flex flex-wrap gap-2">
                  <a data-testid="ad-video-download" href={fileUrl(adVideo.video)} download="publicite-successnet.mp4" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#1B4332] px-3 h-9 rounded-lg"><Download className="h-3.5 w-3.5" /> Télécharger</a>
                  <button data-testid="ad-video-native-share" onClick={shareVideoFile} className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#E85D04] px-3 h-9 rounded-lg"><Share2 className="h-3.5 w-3.5" /> Partager la vidéo</button>
                  <button data-testid="ad-video-wa" onClick={() => shareWhatsApp(adVideo.headline || "Découvrez ma boutique")} className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#25D366] px-3 h-9 rounded-lg"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</button>
                  <button data-testid="ad-video-fb" onClick={() => shareFacebook(adVideo.headline || "Découvrez ma boutique")} className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#1877F2] px-3 h-9 rounded-lg"><Facebook className="h-3.5 w-3.5" /> Facebook</button>
                </div>
                <p className="text-[11px] text-[#9c968a] mt-2">Sur mobile, « Partager la vidéo » envoie directement le fichier. Sinon, téléchargez la vidéo puis publiez-la (Facebook, Instagram, TikTok, statut WhatsApp).</p>
              </div>
            )}
          </div>
        )}
      </section>

      {/* ÉTAPE 3 — Lancement */}
      <section ref={launchRef} className="bg-white rounded-2xl border border-black/5 shadow-sm p-5" data-testid="step-launch">
        <p className="text-[11px] font-bold uppercase tracking-widest text-[#E85D04] mb-1">Étape 3 · Lancement</p>
        <p className="text-sm text-[#5C574F] mb-3">Boostez votre publication sur Meta (Facebook &amp; Instagram) pour toucher des clients près de chez vous.</p>

        {/* Connexion des comptes réseaux sociaux (activation progressive) */}
        {social && (
          <div className="space-y-2 mb-4" data-testid="social-connect">
            {[
              { k: "meta", label: "Facebook & Instagram", Icon: FbIcon, color: "#1877F2" },
              { k: "tiktok", label: "TikTok", Icon: TkIcon, color: "#000000" },
            ].map(({ k, label, Icon, color }) => {
              const s = social[k] || {};
              return (
                <div key={k} className="flex items-center justify-between rounded-xl border border-[#E5E0D8] p-3" data-testid={`social-${k}`}>
                  <span className="flex items-center gap-2 text-sm font-semibold text-[#1A1A1A]">
                    <Icon className="h-4 w-4" style={{ color }} /> {label}
                  </span>
                  {!s.configured ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#9c968a] bg-[#F0ECE4] px-2 py-1 rounded-full" data-testid={`social-${k}-soon`}>
                      <Clock className="h-3 w-3" /> Bientôt disponible
                    </span>
                  ) : s.connected ? (
                    <span className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#1B4332]"><Check2 className="h-3.5 w-3.5" /> Connecté</span>
                      <button data-testid={`social-${k}-boost`} onClick={() => boostAuto(k)} className="text-xs font-bold text-white bg-[#E85D04] px-3 h-8 rounded-lg">Booster</button>
                    </span>
                  ) : (
                    <button data-testid={`social-${k}-connect`} onClick={() => connectSocial(k)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-white px-3 h-8 rounded-lg" style={{ backgroundColor: color }}>
                      <Link2 className="h-3.5 w-3.5" /> Connecter
                    </button>
                  )}
                </div>
              );
            })}
            <p className="text-[11px] text-[#9c968a]">Le lancement 100% automatique depuis SuccèsNet s'active dès l'approbation officielle des plateformes. En attendant, utilisez le mode manuel ci-dessous.</p>
          </div>
        )}

        {selected ? (
          <div className="rounded-xl bg-[#F9F6F0] border border-[#E5E0D8] p-3 mb-3" data-testid="selected-text">
            <p className="text-[11px] font-bold text-[#9c968a] mb-1">Texte sélectionné</p>
            <p className="text-sm text-[#1A1A1A] whitespace-pre-line">{selected}</p>
          </div>
        ) : (
          <p className="text-xs text-[#9c968a] mb-3">Astuce : à l'étape 2, cliquez sur « Booster ce texte » pour choisir votre message.</p>
        )}

        <div className="rounded-xl bg-[#FFF6EE] border border-[#F3D9C2] p-3 mb-3">
          <p className="text-xs font-bold text-[#E85D04] mb-1.5">Comment lancer votre pub à 5$/jour :</p>
          <ol className="text-xs text-[#5C574F] space-y-1 list-decimal pl-4">
            <li>Cliquez sur « Booster sur Meta » — votre texte est copié automatiquement.</li>
            <li>Collez le texte et ajoutez une belle photo de produit.</li>
            <li>Ciblage : centres d'intérêt <b>Mode &amp; achats en ligne</b>, votre ville/zone.</li>
            <li>Budget : <b>5$/jour</b>, durée 5 à 7 jours. Lancez !</li>
          </ol>
        </div>

        <button
          data-testid="boost-meta-btn"
          onClick={openBoost}
          className="w-full h-14 rounded-2xl bg-[#1877F2] text-white font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"
        >
          <Rocket className="h-5 w-5" /> Booster sur Meta (5$/jour) <ExternalLink className="h-4 w-4" />
        </button>
        <p className="text-[11px] text-[#9c968a] mt-2 text-center">Le paiement et le ciblage final se font en toute sécurité sur Meta.</p>
      </section>
    </div>
  );
}
